/**
 * Safety Card Component
 * 
 * Displays LinkedIn safety information in chat.
 */

import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface SafetyPoint {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const safetyPoints: SafetyPoint[] = [
  {
    icon: <LockIcon />,
    title: 'Cookie-Based Auth',
    description: 'We never store your password. You stay in complete control.',
  },
  {
    icon: <SpeedIcon />,
    title: 'Conservative Limits',
    description: 'We stay well below LinkedIn thresholds to protect your account.',
  },
  {
    icon: <AccessTimeIcon />,
    title: 'Human-Like Behavior',
    description: 'Random delays, working hours only, natural patterns.',
  },
];

export const SafetyCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.card}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.shieldIcon}>
          <ShieldIcon />
        </Box>
        <Box>
          <Typography variant="h6" className={classes.title}>
            {t('publicChat.safetyTitle', 'Your Account is Protected')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t('publicChat.safetySubtitle', 'Industry-leading safety measures')}
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box className={classes.stats}>
        <Box className={classes.stat}>
          <Typography variant="h5" className={classes.statValue}>
            50K+
          </Typography>
          <Typography variant="caption" className={classes.statLabel}>
            Users since 2019
          </Typography>
        </Box>
        <Box className={classes.stat}>
          <Typography variant="h5" className={classes.statValue}>
            &lt;0.1%
          </Typography>
          <Typography variant="caption" className={classes.statLabel}>
            Restriction rate
          </Typography>
        </Box>
        <Box className={classes.stat}>
          <Typography variant="h5" className={classes.statValue}>
            24-48h
          </Typography>
          <Typography variant="caption" className={classes.statLabel}>
            Rare issues resolve
          </Typography>
        </Box>
      </Box>

      {/* Trust Score */}
      <Box className={classes.trustScore}>
        <Box className={classes.trustHeader}>
          <Typography variant="subtitle2" className={classes.trustLabel}>
            Account Safety Score
          </Typography>
          <Typography variant="subtitle2" className={classes.trustValue}>
            99.9%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={99.9}
          className={classes.progress}
        />
      </Box>

      {/* Safety Points */}
      <Box className={classes.points}>
        {safetyPoints.map((point, index) => (
          <Box key={index} className={classes.point}>
            <Box className={classes.pointIcon}>
              {point.icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" className={classes.pointTitle}>
                {point.title}
              </Typography>
              <Typography variant="body2" className={classes.pointDescription}>
                {point.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Bottom Note */}
      <Box className={classes.note}>
        <CheckIcon className={classes.noteIcon} />
        <Typography variant="caption" className={classes.noteText}>
          {t('publicChat.safetyNote', 'Most users never experience any issues. We prioritize your account safety above everything.')}
        </Typography>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.success.light}`,
    padding: theme.spacing(3),
    maxWidth: 500,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  header: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  shieldIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `${theme.palette.success.main}1A`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: 28,
      color: theme.palette.success.main,
    },
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  stats: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(2),
    backgroundColor: `${theme.palette.success.main}0A`,
    borderRadius: 12,
    marginBottom: theme.spacing(3),
  },
  stat: {
    textAlign: 'center',
  },
  statValue: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  statLabel: {
    color: theme.palette.text.secondary,
  },
  trustScore: {
    marginBottom: theme.spacing(3),
  },
  trustHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  trustLabel: {
    color: theme.palette.text.secondary,
  },
  trustValue: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  progress: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.greys.lighter,
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.success.main,
      borderRadius: 4,
    },
  },
  points: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  point: {
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  pointIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: theme.palette.backgrounds.main_new,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      fontSize: 18,
      color: theme.palette.success.main,
    },
  },
  pointTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  pointDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  note: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: `${theme.palette.success.main}0A`,
    borderRadius: 8,
  },
  noteIcon: {
    fontSize: 18,
    color: theme.palette.success.main,
    flexShrink: 0,
    marginTop: 2,
  },
  noteText: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
}));

export default SafetyCard;

